import { X, Clock, Zap, Check, ExternalLink, Shield, Sparkles, TrendingUp, Mail, MessageCircle } from "lucide-react";
import { useState, useEffect } from "react";

const Services = () => {
  const [showModal, setShowModal] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [activeService, setActiveService] = useState(0);

  const services = [
    {
      name: "Doctor Portfolio",
      description: "Specialized template for medical professionals to showcase services and manage appointments.",
      image: "/assets/doctor-portfolio-image.png",
      video: "/assets/doctor-portfolio-video.webm",
      link: "https://portfolio-doctor.netlify.app/",
      originalPrice: 19999,
      discountedPrice: 9999,
      features: ["Appointment booking", "Service listings", "Doctor bio section", "WhatsApp integration", "Patient testimonials"],
      tag: "Specialized",
      color: "from-green-500 to-emerald-400",
      icon: <Sparkles className="w-5 h-5" />
    },
    {
      name: "Basic Website",
      description: "Perfect for individuals starting their online presence with a simple yet effective portfolio.",
      image: "/assets/short-template-image.png",
      video: "/assets/short-template-video.webm",
      link: "https://short-template.netlify.app/",
      originalPrice: 4999,
      discountedPrice: 1999,
      features: ["Single page design", "Contact form integration", "Mobile responsive", "Basic SEO", "Social media links"],
      tag: "Most Popular",
      color: "from-blue-500 to-cyan-400",
      icon: <Sparkles className="w-5 h-5" />
    },
    {
      name: "Standard Website",
      description: "Ideal for professionals who need to showcase their work and services with multiple sections.",
      image: "/assets/standard-template-image.png",
      video: "/assets/standard-template-video.webm",
      link: "https://standard-template.netlify.app",
      originalPrice: 7999,
      discountedPrice: 3499,
      features: ["Multi-section layout", "Project gallery", "Testimonials section", "Advanced contact form", "Blog setup"],
      tag: "Best Value",
      color: "from-purple-500 to-pink-400",
      icon: <TrendingUp className="w-5 h-5" />
    },
    {
      name: "Premium Website",
      description: "Advanced solution with all bells and whistles for serious professionals and businesses.",
      image: "/assets/premium-template-image.png",
      video: "/assets/premium-template-video.webm",
      link: "https://premium-templatee.netlify.app/",
      originalPrice: 17999,
      discountedPrice: 8999,
      features: ["Custom animations", "Advanced SEO", "Blog with CMS", "Custom domain setup", "Analytics integration"],
      tag: "Feature Rich",
      color: "from-orange-500 to-red-400",
      icon: <Shield className="w-5 h-5" />
    },

  ];

  // Modal trigger effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.3 && isFirstTime) {
            setTimeout(() => {
              setShowModal(true);
            }, 1000);
          }
        });
      },
      {
        threshold: 0.3,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    const servicesElement = document.getElementById("services");
    if (servicesElement) {
      observer.observe(servicesElement);
    }

    return () => {
      if (servicesElement) {
        observer.unobserve(servicesElement);
      }
    };
  }, [isFirstTime]);

  const closeModal = () => {
    setShowModal(false);
  };

  const redirectModal = () => {
    setShowModal(false);
    setIsFirstTime(false);
    const servicesSection = document.getElementById("services");
    if (servicesSection) {
      setTimeout(() => {
        servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // Generate WhatsApp message for selected package
  const getWhatsAppMessage = (service) => {
    const message = `Hi! I'm interested in the *${service.name}* package.\n\n📋 Package Details:\n${service.description}\n\n✨ Features:\n${service.features.map((f, i) => `${i + 1}. ${f}`).join('\n')}\n\nI'd like to know more about this package and discuss how we can proceed. Thank you!`;
    const phoneNumber = "919426631080"; // Replace with your WhatsApp number (with country code, no + or spaces)
    return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
  };

  // Generate email for selected package
  const getEmailLink = (service) => {
    const subject = `Inquiry about ${service.name}`;
    const body = `Hi,\n\nI'm interested in the ${service.name} package.\n\n📋 Package Details:\n${service.description}\n\n✨ Features:\n${service.features.map((f, i) => `${i + 1}. ${f}`).join('\n')}\n\nI'd like to know more about this package and discuss how we can proceed.\n\nThank you!`;
    const email = "jskolawala@gmail.com"; // Replace with your email address
    return `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div id="services" className="py-16 px-4 sm:px-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-[#5e7daf]/20 to-[#38bdf8]/20 rounded-full mb-4">
          <span className="text-[#38bdf8] font-semibold text-sm philosopher-6">OUR OFFERINGS</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-[#e2e8f0] mb-6 orbitron">
          Premium Website Packages
        </h2>
        <p className="text-lg text-[#94a3b8] max-w-2xl mx-auto philosopher-5">
          Choose from our carefully crafted website solutions designed to elevate your online presence
        </p>
      </div>

      {/* Services Grid - Horizontal Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
        {/* Left Column - Service Cards */}
        <div className="space-y-6">
          {services.map((service, index) => {
            return (
              <div
                key={index}
                onClick={() => setActiveService(index)}
                className={`bg-[#1e293b] rounded-2xl p-6 cursor-pointer transition-all duration-300 border-2 ${activeService === index
                  ? 'border-[#5e7daf] shadow-xl shadow-[#5e7daf]/20'
                  : 'border-transparent hover:border-[#5e7daf]/50'
                  }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${service.color}`}>
                      {service.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-[#e2e8f0] philosopher-6">{service.name}</h3>
                      {service.tag && (
                        <span className={`text-xs px-3 py-1 rounded-full bg-gradient-to-r ${service.color} text-white mt-1 inline-block`}>
                          {service.tag}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <p className="text-[#94a3b8] mb-4 philosopher-5 text-sm">
                  {service.description}
                </p>

                <div className="flex items-center justify-between">
                  <div></div>
                  <a
                    href={service.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-[#38bdf8] hover:text-[#5e7daf] flex items-center space-x-1 philosopher-6"
                    onClick={(e) => e.stopPropagation()}
                  >
                  </a>
                </div>
              </div>
            );
          })}
        </div>

        {/* Right Column - Active Service Details */}
        <div className="bg-gradient-to-br from-[#1e293b] to-[#0f172a] rounded-2xl p-8 border border-[#5e7daf]/30">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold text-[#e2e8f0] orbitron">
                {services[activeService].name}
              </h3>
              <p className="text-[#94a3b8] mt-1 philosopher-5">
                {services[activeService].tag}
              </p>
            </div>
          </div>

          {/* Preview Image/Video */}
          <div className="relative group overflow-hidden rounded-xl mb-8">
            <img
              src={services[activeService].image}
              alt={services[activeService].name}
              className="w-full h-64 object-cover"
            />
            {services[activeService].video && (
              <video
                className="absolute top-0 left-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition duration-500"
                src={services[activeService].video}
                autoPlay
                loop
                muted
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-4">
              <a
                href={services[activeService].link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-white text-black px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors philosopher-6"
              >
                <ExternalLink size={18} />
                <span>View Live Website</span>
              </a>
            </div>
          </div>

          {/* Features List */}
          <div className="mb-8">
            <h4 className="text-lg font-semibold text-[#e2e8f0] mb-4 philosopher-6">
              What's Included
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {services[activeService].features.map((feature, index) => (
                <div key={index} className="flex items-center space-x-3 bg-[#0f172a]/50 p-3 rounded-lg">
                  <div className={`p-1 rounded-full bg-gradient-to-r ${services[activeService].color}`}>
                    <Check size={14} className="text-white" />
                  </div>
                  <span className="text-[#e2e8f0] philosopher-5 text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Buttons - WhatsApp and Email */}
          <div className="bg-gradient-to-r from-[#5e7daf]/10 to-[#38bdf8]/10 border border-[#5e7daf]/20 rounded-xl p-6">
            <p className="text-[#94a3b8] mb-4 philosopher-5 text-sm text-center">
              Ready to launch your professional website?
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={getWhatsAppMessage(services[activeService])}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg philosopher-6"
              >
                <MessageCircle size={20} />
                <span>WhatsApp</span>
              </a>
              <a
                href={getEmailLink(services[activeService])}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#5e7daf] to-[#38bdf8] hover:from-[#4a6490] hover:to-[#2b9bd9] text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 hover:scale-105 shadow-lg philosopher-6"
              >
                <Mail size={20} />
                <span>Email</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Limited Time Offer Modal */}
      {showModal && isFirstTime && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#1e293b] to-[#0f172a] border border-[#5e7daf]/30 rounded-2xl p-8 max-w-lg w-full relative shadow-2xl animate-[fadeIn_0.5s_ease-out]">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-[#94a3b8] hover:text-[#e2e8f0] transition-colors duration-200"
            >
              <X size={24} />
            </button>

            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="bg-gradient-to-r from-[#5e7daf] to-[#38bdf8] p-3 rounded-full">
                  <Zap className="text-white" size={32} />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-[#e2e8f0] mb-2 orbitron">
                🎉 Limited Time Offer!
              </h3>

              <p className="text-[#94a3b8] mb-6 philosopher-5 leading-relaxed">
                Get exclusive discounts on all our premium website packages. Upgrade your online presence today!
              </p>

              <div className="bg-gradient-to-r from-[#5e7daf]/20 to-[#38bdf8]/20 border border-[#5e7daf]/40 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center mb-2">
                  <Clock className="text-[#38bdf8] mr-2" size={20} />
                  <span className="text-[#38bdf8] font-semibold philosopher-6">
                    Special Launch Pricing
                  </span>
                </div>
                <p className="text-[#e2e8f0] text-sm philosopher-5">
                  Prices increase soon - lock in your rate today!
                </p>
              </div>

              <button
                onClick={redirectModal}
                className="bg-gradient-to-r from-[#5e7daf] to-[#38bdf8] hover:from-[#4a6490] hover:to-[#2b9bd9] text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg philosopher-6"
              >
                View All Packages
              </button>

              <p className="text-[#94a3b8] text-xs mt-4 philosopher-5">
                Offer ends soon - don't miss out!
              </p>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
};

export default Services;